import { useEffect, useState, useMemo, useCallback, useRef, useLayoutEffect } from "react";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import { useDrillHistoryBack } from "../../hooks/useDrillHistoryBack";
import { useSwipeBack } from "../../hooks/useSwipeBack";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar from "../layout/TopBar";
import BottomNav from "../BottomNav";
import { useSharedReportDateFilter } from "../../redux/reportFilters/reportFiltersHooks";
import { todayStr, yesterdayStr, getStartEndFromFilter } from "../../services/reportsApi";
import { useStoreTimeZone } from "../../utils/dateTimeUtils";
import { comparisonTooltip, hasComparison, exportPeriodFor } from "../../utils/reportPeriod";
import { useCurrencySymbol } from "../../utils/currency";
import { getCurrencySymbol } from "../../utils/currency";
import { formatMoneyForExport } from "../../utils/currency";
import "./reports.css";
import "./LaborReport.css";
import { T, useIsDesktop, Bone } from "./reportTokens";
import { ReportSectionExportButton } from "./components/ReportExportButton";
import { DataTableCard, SectionCard, type Col, type ServerPagination } from "./components/DataTableCard";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchLabourEmployeeSalesRequest, fetchLabourTipsRequest, fetchLabourVoidsRequest, fetchLabourWorkingHoursRequest, fetchLabourStaffPerformanceRequest, fetchLabourWorkingHoursLogRequest, fetchLabourKpiComparisonRequest, fetchLabourEmployeesRequest } from "../../redux/reports/clickHouse/clickHouseActions";
import { getLabourStaffPerformanceApi, getLabourWorkingHoursLogApi } from "../../redux/reports/clickHouse/clickHouseApi";
import type { LabourKpiComparisonResponse, LabourStaffPerformanceItem, LabourWorkingHoursLogItem } from "../../interface/clickHouseInterface";
import { useAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";
import KpiTooltip from "../KpiTooltip";
/* ─── Types ───────────────────────────────────────────────────────────────── */
interface LaborShift {
    id: string;
    empId: string;
    empName: string;
    date: string;
    clockIn: Date | string;
    clockOut: Date | string | null;
    breakMinutes: number;
    hours: number;
    sales: number;
    tips: number;
    gratuity: number;
    orders: number;
    voids: { reason: string; amount: number }[];
    anomalyZero?: boolean;
}

interface EmployeeAgg {
    empId: string;
    empName: string;
    hours: number;
    breakHours: number;
    sales: number;
    tips: number;
    gratuity: number;
    orders: number;
    voidCount: number;
    voidAmount: number;
    daysWorked: number;
    splh: number | null;
    tipRate: number | null;
}

interface LaborAgg {
    totalHours: number;
    totalBreakHours: number;
    staffWorked: number;
    totalSales: number;
    netSales: number;
    totalTips: number;
    totalGratuity: number;
    voidCount: number;
    voidAmount: number;
    splh: number | null;
    employees: EmployeeAgg[];
    shifts: LaborShift[];
    voidReasons: { reason: string; count: number; amount: number }[];
}

interface LaborKpi {
    key: 'hours' | 'staff' | 'splh' | 'sales' | 'tips' | 'voids';
    label: string;
    mobileLabel: string;
    value: string;
    shortValue?: string;
    subtext?: string;
    delta: number | null;
    invertDelta?: boolean;
    drillable: boolean;
}

interface LaborData {
    kpis: LaborKpi[];
    agg: LaborAgg;
    multiDay: boolean;
    priorLabel: string;
    rangeLabel: string;
    isEmpty: boolean;
}

interface DateRange { start: Date; end: Date; multiDay: boolean; label: string; }

/* ─── Formatting ──────────────────────────────────────────────────────────── */
const isoDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtHours = (decimal: number): string => {
    if (!Number.isFinite(decimal) || decimal <= 0) return '0h 0m';
    const totalMin = Math.round(decimal * 60);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h.toLocaleString()}h ${m}m`;
};

const fmtMoney = (n: number, symbol = '$'): string =>
    `${symbol}${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtMoneyShort = (n: number, symbol = '$'): string => {
    const v = n || 0;
    if (v > 9999) return `${symbol}${(v / 1000).toFixed(1)}k`;
    return fmtMoney(v, symbol);
};

/* ─── Date range helpers (period labels + multiDay flag) ─────────────────── */
const _DAY_MS = 86400000;
const _startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const _addDays = (d: Date, n: number) => new Date(d.getTime() + n * _DAY_MS);

function rangeForPreset(period: string): { cur: DateRange; prior: DateRange } {
    const today = _startOfDay(new Date());
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const fmt = (d: Date) => `${monthNames[d.getMonth()]} ${d.getDate()}`;

    if (period.startsWith('custom:')) {
        const [, from, to] = period.split(':');
        const start = _startOfDay(new Date(from + 'T12:00:00'));
        const end = _startOfDay(new Date(to + 'T12:00:00'));
        const spanDays = Math.round((end.getTime() - start.getTime()) / _DAY_MS) + 1;
        const priorEnd = _addDays(start, -1);
        const priorStart = _addDays(priorEnd, -(spanDays - 1));
        return {
            cur: { start, end, multiDay: spanDays > 1, label: `${fmt(start)} – ${fmt(end)}` },
            prior: { start: priorStart, end: priorEnd, multiDay: spanDays > 1, label: '' },
        };
    }

    switch (period) {
        case 'Yesterday': {
            const y = _addDays(today, -1);
            return {
                cur: { start: y, end: y, multiDay: false, label: 'Yesterday' },
                prior: { start: _addDays(today, -2), end: _addDays(today, -2), multiDay: false, label: '' },
            };
        }
        case '7D':
            return {
                cur: { start: _addDays(today, -6), end: today, multiDay: true, label: 'Last 7 Days' },
                prior: { start: _addDays(today, -13), end: _addDays(today, -7), multiDay: true, label: '' },
            };
        case '30D':
            return {
                cur: { start: _addDays(today, -29), end: today, multiDay: true, label: 'Last 30 Days' },
                prior: { start: _addDays(today, -59), end: _addDays(today, -30), multiDay: true, label: '' },
            };
        case 'This Month': {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const prevEnd = _addDays(start, -1);
            return {
                cur: { start, end: today, multiDay: true, label: 'This Month' },
                prior: { start: prevStart, end: prevEnd, multiDay: true, label: '' },
            };
        }
        case 'Today':
        default:
            return {
                cur: { start: today, end: today, multiDay: false, label: 'Today' },
                prior: { start: _addDays(today, -1), end: _addDays(today, -1), multiDay: false, label: '' },
            };
    }
}

// ZAR-1661: delta visibility follows the SHAPE of the selected period. This replaced a
// hardcoded `['today', 'yesterday', '7d']` list, whose premise — that only short,
// fixed-length windows compare meaningfully — the shape registry supersedes: a whole
// calendar month compares to the whole prior month, month-to-date compares over equal
// elapsed days, and a stepper emits `custom:from:to` tokens no literal list could cover.
// Sharing the resolver with the saga also keeps this gate and the comp window actually
// sent to `/sales/labour/kpi-comparison` from drifting apart.
const allowsComparison = (period: string): boolean => hasComparison(period || 'Today');

// Human-readable active date range for the "All outlets" export file header —
// preset names (e.g. "Today", "7D") pass through as-is; "custom:start:end" formats nicely.
// The export header's date range comes from `exportPeriodFor` in utils/reportPeriod
// (DPFIX-07). The local copy that lived here returned a label only, never the year, and
// was one of seven that had already drifted apart — see DPFIX-05, where Snapshot's copy
// still tested the deleted 'Custom' token and leaked the raw one into a file.

/**
 * GLOBAL-DP-4 (ZAR-1663): the per-card comparison label is removed — the basis is stated
 * once in the unified date control. What each KPI keeps is the resolved comparison period
 * as a hover tooltip, so the basis is still recoverable on an individual card.
 */
const getPriorLabel = (period: string): string => comparisonTooltip(period || 'Today');

/* ─── Zero aggregate + KPI skeleton (all live values come from API) ───────── */
const ZERO_AGG: LaborAgg = {
    totalHours: 0, totalBreakHours: 0, staffWorked: 0,
    totalSales: 0, netSales: 0, totalTips: 0, totalGratuity: 0,
    voidCount: 0, voidAmount: 0, splh: null,
    employees: [], shifts: [], voidReasons: [],
};

function buildLaborData(period: string, _employeeId: string | null, symbol = '$'): LaborData {
    const { cur } = rangeForPreset(period);
    const kpis: LaborKpi[] = [
        { key: 'hours', label: 'Total Working Hours', mobileLabel: 'WORK HOURS', value: '0h 0m', delta: null, drillable: true },
        { key: 'staff', label: 'Staff Worked', mobileLabel: 'STAFF', value: '0', delta: null, drillable: false },
        { key: 'splh', label: 'SPLH', mobileLabel: 'SPLH', value: '—', delta: null, drillable: false },
        { key: 'sales', label: 'Employee Item Sales', mobileLabel: 'EMP SALES', value: `${symbol}0.00`, delta: null, drillable: true },
        { key: 'tips', label: 'Total Tips', mobileLabel: 'TIPS', value: `${symbol}0.00`, delta: null, drillable: true },
        { key: 'voids', label: 'Voided Items', mobileLabel: 'VOIDED', value: '0', delta: null, invertDelta: true, drillable: true },
    ];
    return {
        kpis, agg: ZERO_AGG, multiDay: cur.multiDay,
        priorLabel: getPriorLabel(period), rangeLabel: cur.label, isEmpty: true,
    };
}


const findToken = (obj: any, depth = 0): string | null => {
    if (depth > 6 || !obj || typeof obj !== "object") return null;
    for (const key of ["token", "access_token", "accessToken", "id_token"]) {
        if (typeof obj[key] === "string" && obj[key].length > 20) return obj[key];
    }
    for (const val of Object.values(obj)) {
        const found = findToken(val, depth + 1);
        if (found) return found;
    }
    return null;
};

const findLocationId = (obj: any, depth = 0): string | null => {
    if (depth > 6 || !obj || typeof obj !== "object") return null;
    for (const key of ["locationId", "location_id", "locationID", "locId", "outletId", "outlet_id"]) {
        if (typeof obj[key] === "string" && obj[key].length > 4) return obj[key];
    }
    for (const val of Object.values(obj)) {
        const found = findLocationId(val, depth + 1);
        if (found) return found;
    }
    return null;
};

// Converts API "Xh Ym" string to decimal hours number for EmployeeAgg
const parseHoursStr = (s: string): number => {
    if (!s) return 0;
    const m = s.match(/(\d+)h\s*(\d+)m/);
    if (!m) return parseFloat(s) || 0;
    return parseInt(m[1]) + parseInt(m[2]) / 60;
};

// API "Xh Ym" strings come pre-formatted with no thousands separator. Re-parse
// + re-format through fmtHours so hour totals ≥1000 render as "1,234h 15m".
const fmtHoursStr = (s: string | null | undefined): string => (s ? fmtHours(parseHoursStr(s)) : '0h 0m');

const mapStaffPerformance = (items: LabourStaffPerformanceItem[]): EmployeeAgg[] =>
    items.map((item) => ({
        empId: item.employeeName,
        empName: item.employeeName,
        hours: parseHoursStr(item.totalHours),
        breakHours: parseHoursStr(item.breakHours),
        sales: item.sales ?? 0,
        splh: item.splh ?? null,
        orders: 0,
        tips: item.tips ?? 0,
        tipRate: null,
        gratuity: item.gratuity ?? 0,
        voidCount: item.voids ?? 0,
        voidAmount: item.voidAmount ?? 0,
        daysWorked: 1,
    }));

// Matches the on-screen "Total" footer row — prefers server-computed summary
// (accurate for the whole filtered dataset) and falls back to summing `list`.
const buildStaffPerformanceTotalRow = (list: EmployeeAgg[], summary?: any): (string | number)[] => {
    const totalHours = summary ? fmtHoursStr(summary.totalHours) : fmtHours(list.reduce((a, r) => a + r.hours, 0));
    const totalBreakHours = summary ? fmtHoursStr(summary.totalBreakHours) : fmtHours(list.reduce((a, r) => a + r.breakHours, 0));
    const totalSales = formatMoneyForExport(summary ? summary.totalSales : list.reduce((a, r) => a + r.sales, 0));
    const totalSplh = summary?.totalSplh != null ? formatMoneyForExport(summary.totalSplh) : "—";
    const totalTips = formatMoneyForExport(summary ? summary.totalTips : list.reduce((a, r) => a + r.tips, 0));
    const totalGratuity = formatMoneyForExport(summary ? summary.totalGratuity : list.reduce((a, r) => a + r.gratuity, 0));
    const totalVoids = summary ? summary.totalVoids : list.reduce((a, r) => a + r.voidCount, 0);
    const totalVoidAmount = formatMoneyForExport(summary ? summary.totalVoidAmount : list.reduce((a, r) => a + r.voidAmount, 0));
    return ["Total", totalHours, totalBreakHours, totalSales, totalSplh, totalTips, totalGratuity, totalVoids, totalVoidAmount];
};

const mapWorkingHoursLog = (items: LabourWorkingHoursLogItem[]): LaborShift[] =>
    items.map((item) => ({
        id: item.employeeName + item.workDate,
        empId: item.employeeName,
        empName: item.employeeName,
        date: item.workDate ?? "",
        clockIn: item.clockIn,
        clockOut: item.status == "In Progress" ? null : (item.clockOut ?? null),
        hours: parseHoursStr(item.workedHours),
        breakMinutes: Math.round(parseHoursStr(item.breakHours) * 60),
        sales: 0,
        tips: 0,
        gratuity: 0,
        orders: 0,
        voids: [],
        anomalyZero: parseHoursStr(item.workedHours) === 0 && item.clockOut !== null,
    }));

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — LABOR & WORKFORCE REPORT  (P0)
   Responsive: Desktop (≥900px) + Mobile (<900px). Frontend + sample data only.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─── GA4 (no backend — pushes to dataLayer if present) ──────────────────── */
const track = (event: string, params: Record<string, any> = {}) => {
    try {
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({ event, report_group: "Team", report_name: "Labor", platform: window.innerWidth >= 900 ? "desktop" : "mobile", ...params });
    } catch { /* noop */ }
};

const DRILL_TITLES: Record<string, string> = {
    hours: "Total Hours", sales: "Employee Sales",
    tips: "Tips Breakdown", voids: "Voids Breakdown",
};

const fmtClock = (d: Date | string | null): string => {
    if (!d) return "In Progress";
    if (typeof d === "string") return d;
    let h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, "0");
    const ap = h >= 12 ? "PM" : "AM";
    h = h % 12; if (h === 0) h = 12;
    return `${String(h).padStart(2, "0")}:${m} ${ap}`;
};
// Converts "HH:MM AM/PM" or Date to minutes-since-midnight for sorting
const clockToNum = (c: Date | string | null): number => {
    if (!c) return Infinity;
    if (c instanceof Date) return c.getHours() * 60 + c.getMinutes();
    const match = String(c).match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return 0;
    let h = parseInt(match[1]);
    const min = parseInt(match[2]);
    if (match[3].toUpperCase() === "PM" && h !== 12) h += 12;
    if (match[3].toUpperCase() === "AM" && h === 12) h = 0;
    return h * 60 + min;
};
const fmtShiftDate = (iso: string | undefined): string => {
    if (!iso) return "—";
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const datePart = iso.includes("T") ? iso.split("T")[0] : iso;
    const [y, m, d] = datePart.split("-").map(Number);
    if (!y || !m || !d) return iso;
    return `${months[m - 1]} ${String(d).padStart(2, "0")}, ${y}`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

// Matches SnapshotScreen's drill-down affordance — small chevron, top-right of the tile.
const DrillArrow = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 6, right: 6, opacity: 0.45 }}>
        <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const KPI_TOOLTIPS: Partial<Record<string, string>> = {
    splh: "Sales Per Labour Hour calculated by dividing net sales by labour hour",
    sales: "Net Sales which excludes taxes and all deductions",
    voids: "Items that were sent to the kitchen but removed before the guest was served and before any payment was taken.",
};

function KPICard({ kpi, priorLabel, onTap, showDelta }: any) {
    const isDesktop = useIsDesktop();
    const { key, label, mobileLabel, value, shortValue, subtext, delta, invertDelta, drillable } = kpi;
    const tooltipText = KPI_TOOLTIPS[key];
    const safe = Number(delta) || 0;
    // Rendered exactly as SnapshotScreen's `getDeltaNode` renders the same numbers
    // (DPFIX-06), so the three reports that carry a comparison do not each state it
    // differently. Two decimals, an explicit sign, and — for a "lower is better" metric
    // like Voids (`invertDelta`) — the COLOR flips to convey good/bad while the ARROW
    // matches the actual sign of the value (same as Cancelled/Refund on Snapshot and
    // Sales — the arrow is never tied to good/bad, only the color is), so "↓ -5.00%"
    // reads "voids fell 5%, which is good" and "↑ +5.00%" reads "voids rose 5%, which
    // is bad" — the arrow and the number always agree.
    //
    // **There is no "N/A" state.** `Number(null) || 0` is 0, so a delta the labour endpoint
    // could not express as a percentage reads "0.00%" like any other flat period. That is a
    // product decision, taken knowingly: the endpoint returns null both when nothing happened
    // in either period AND when a KPI grew out of nothing (0 → n, where the percentage change
    // is undefined), and the second case therefore reads as no change. Snapshot and Sales have
    // no N/A either — their endpoint answers 0 — and matching them was the point.
    //
    // What changed beyond the format: a non-inverted KPI used to paint green for any
    // nonzero change, so a fall in tips looked like good news. It now follows the sign.
    const isGood = invertDelta ? safe < 0 : safe >= 0;
    const deltaColor = safe === 0 ? T.t2 : isGood ? T.green : T.red;
    const deltaText = safe === 0
        ? "0.00%"
        : `${safe >= 0 ? "↑" : "↓"} ${safe >= 0 ? "+" : "-"}${Math.abs(safe).toFixed(2)}%`;

    // Desktop-only: fall back to the compact value (e.g. "$4.7k") when the full
    // comma-separated value doesn't fit the card width, instead of ellipsing it.
    const valueBoxRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const [fitsFull, setFitsFull] = useState(true);

    useLayoutEffect(() => {
        if (!isDesktop || !shortValue) return;
        const box = valueBoxRef.current;
        const measure = measureRef.current;
        if (!box || !measure) return;
        const check = () => setFitsFull(measure.scrollWidth <= box.clientWidth);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(box);
        return () => ro.disconnect();
    }, [isDesktop, shortValue, value]);

    const displayValue = isDesktop && shortValue && !fitsFull ? shortValue : value;

    if (isDesktop) {
        return (
            <div
                onClick={drillable ? onTap : undefined}
                style={{
                    flex: "1 1 0", minWidth: 0, position: "relative", overflow: "hidden",
                    background: T.surface, borderRadius: 14, padding: "18px 18px",
                    border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`,
                    boxShadow: T.cardShadow, cursor: drillable ? "pointer" : "default",
                }}
            >
                {drillable && <DrillArrow />}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, position: "relative" }}>
                    <span style={{ fontSize: 11, fontWeight: 500, color: T.t2, textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center", gap: 4 }}>
                        {label}
                        {tooltipText && <KpiTooltip text={tooltipText} />}
                    </span>
                </div>
                <div ref={valueBoxRef} style={{ fontSize: 25, fontWeight: 700, color: T.t1, letterSpacing: -0.5, position: "relative", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {shortValue && (
                        <span ref={measureRef} style={{ position: "absolute", visibility: "hidden", whiteSpace: "nowrap", pointerEvents: "none" }}>{value}</span>
                    )}
                    {displayValue}
                </div>
                {subtext && <div style={{ fontSize: 11, fontWeight: 500, color: T.t3, marginTop: 3, position: "relative" }}>{subtext}</div>}
                {showDelta && (
                    <div title={priorLabel} style={{ fontSize: 12, fontWeight: 600, color: deltaColor, marginTop: 4, position: "relative" }}>
                        {deltaText}
                    </div>
                )}
            </div>
        );
    }
    return (
        <div
            onClick={drillable ? onTap : undefined}
            style={{
                position: "relative", background: T.surface, borderRadius: 12, padding: "12px 10px",
                border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`, boxShadow: T.shadow,
                cursor: drillable ? "pointer" : "default", minWidth: 0,
            }}
        >
            {drillable && <DrillArrow />}
            <div style={{ fontSize: 8, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3, display: "flex", alignItems: "center", gap: 3 }}>
                {mobileLabel}
                {tooltipText && <KpiTooltip text={tooltipText} isMobile />}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.t1, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
            {subtext && <div style={{ fontSize: 9, fontWeight: 500, color: T.t3, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{subtext}</div>}
            {showDelta && <div style={{ fontSize: 9, fontWeight: 600, color: deltaColor, marginTop: 3 }}>{deltaText}</div>}
        </div>
    );
}

function MiniKpiRow({ items }: { items: { label: string; value: string; color?: string; tooltip?: string }[] }) {
    const isDesktop = useIsDesktop();
    return (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: isDesktop ? 16 : 8, marginBottom: 16 }}>
            {items.map((it, i) => (
                <div key={i} style={{ textAlign: "center", padding: isDesktop ? "16px 12px" : "12px 8px", background: T.hover, borderRadius: isDesktop ? 12 : 10, border: `1px solid ${T.cardBorder}` }}>
                    <div style={{ fontSize: isDesktop ? 11 : 8, color: T.t3, textTransform: "uppercase", letterSpacing: 0.4, display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
                        {it.label}
                        {it.tooltip && <KpiTooltip text={it.tooltip} isMobile={!isDesktop} />}
                    </div>
                    <div style={{ fontSize: isDesktop ? 24 : 17, fontWeight: 700, color: it.color || T.t1, marginTop: isDesktop ? 6 : 3 }}>{it.value}</div>
                </div>
            ))}
        </div>
    );
}

function RankedRow({ rank, name, value, secondary, pct, color, dimmed }: any) {
    const isDesktop = useIsDesktop();
    return (
        <div style={{ padding: isDesktop ? "10px 0" : "8px 0", borderBottom: `1px solid ${T.divider}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
                <span style={{ fontSize: isDesktop ? 13 : 12, fontWeight: dimmed ? 500 : 600, color: dimmed ? T.t2 : T.accent, fontStyle: dimmed ? "italic" : "normal" }}>
                    {rank != null && <span style={{ color: T.t3, fontWeight: 500, marginRight: 6 }}>{rank}</span>}{name}
                </span>
                <span style={{ fontSize: isDesktop ? 13 : 12, fontWeight: 700, color: T.t1 }}>{value}</span>
            </div>
            <div style={{ height: isDesktop ? 6 : 5, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, pct))}%`, background: color, borderRadius: 3 }} />
            </div>
            {secondary && <div style={{ fontSize: isDesktop ? 11 : 9, color: T.t3, marginTop: 4 }}>{secondary}</div>}
        </div>
    );
}


/* ─── Employee filter dropdown (used in topbar + mobile content) ─────────── */
function EmployeeFilter({ options, value, onChange, compact }: any) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);
    const selected = value ? options.find((o: any) => o.id === value)?.name ?? "Employee" : "All Employees";
    const list = useMemo(() => {
        const term = q.trim().toLowerCase();
        return term ? options.filter((o: any) => o.name.toLowerCase().includes(term)) : options;
    }, [options, q]);
    return (
        <div ref={ref} style={{ position: "relative", width: compact ? "100%" : undefined }}>
            <button type="button" onClick={() => setOpen((o) => !o)}
                style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8,
                    border: `1px solid ${value ? `${T.accent}55` : T.cardBorder}`, background: value ? T.accentSoft : T.surface,
                    color: value ? T.accent : T.t2, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: T.font,
                    width: compact ? "100%" : undefined, justifyContent: compact ? "space-between" : undefined,
                }}>
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>person</span>
                <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected}</span>
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>expand_more</span>
            </button>
            {open && (
                <div style={{ position: "absolute", top: "100%", right: compact ? undefined : 0, left: compact ? 0 : undefined, marginTop: 6, width: 240, maxWidth: "90vw", background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 300, overflow: "hidden" }}>
                    {options.length > 8 && (
                        <div style={{ padding: 8, borderBottom: `1px solid ${T.divider}` }}>
                            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee"
                                style={{ width: "100%", border: `1px solid ${T.cardBorder}`, borderRadius: 6, padding: "6px 8px", fontSize: 12, outline: "none", fontFamily: T.font, boxSizing: "border-box" }} />
                        </div>
                    )}
                    <div style={{ maxHeight: 280, overflowY: "auto" }}>
                        {[{ id: null, name: "All Employees" }, ...list].map((o: any) => {
                            const active = (o.id ?? null) === (value ?? null);
                            return (
                                <div key={o.id ?? "all"} onClick={() => { onChange(o.id); setOpen(false); setQ(""); track("maghil_reports_filter_change", { filter_type: "employee", filter_value: o.id ? o.name : "all" }); }}
                                    style={{ padding: "10px 12px", fontSize: 12, cursor: "pointer", background: active ? T.accentSoft : "transparent", color: active ? T.accent : T.t1, fontWeight: active ? 600 : 400 }}>
                                    {o.name}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI GRID
   ═══════════════════════════════════════════════════════════════════════════ */
const KPI_DELTA_FIELD: Record<LaborKpi['key'], 'totalWorkingHoursDelta' | 'staffWorkedDelta' | 'employeeSalesDelta' | 'splhDelta' | 'totalTipsDelta' | 'voidsDelta'> = {
    hours: 'totalWorkingHoursDelta',
    staff: 'staffWorkedDelta',
    splh: 'splhDelta',
    sales: 'employeeSalesDelta',
    tips: 'totalTipsDelta',
    voids: 'voidsDelta',
};

function KpiGrid({ data, onDrill, workingHoursOverride, employeeSalesOverride, voidsOverride, comparisonOverride, staffCountOverride, splhOverride, showDelta }: { data: LaborData; onDrill: (k: string) => void; workingHoursOverride?: { totalHours: string } | null; staffCountOverride?: number | null; splhOverride?: number | null; employeeSalesOverride?: { avgSales: number | null; avgTips: number | null } | null; voidsOverride?: { totalVoids: number; totalVoidAmount: number; itemsTotal: number } | null; comparisonOverride?: LabourKpiComparisonResponse | null; showDelta?: boolean }) {
    const isDesktop = useIsDesktop();
    const sym = useCurrencySymbol();
    const kpis = data.kpis.map((k) => {
        let updated = k;
        if (workingHoursOverride) {
            if (k.key === "hours") updated = { ...updated, value: workingHoursOverride.totalHours };
        }
        if (staffCountOverride != null) {
            if (k.key === "staff") updated = { ...updated, value: String(staffCountOverride) };
        }
        if (splhOverride != null) {
            if (k.key === "splh") updated = { ...updated, value: fmtMoney(splhOverride, sym), shortValue: fmtMoneyShort(splhOverride, sym) };
        }
        if (employeeSalesOverride) {
            if (k.key === "sales" && employeeSalesOverride.avgSales != null) updated = { ...updated, value: isDesktop ? fmtMoney(employeeSalesOverride.avgSales, sym) : fmtMoneyShort(employeeSalesOverride.avgSales, sym), shortValue: fmtMoneyShort(employeeSalesOverride.avgSales, sym) };
            if (k.key === "tips" && employeeSalesOverride.avgTips != null) updated = { ...updated, value: isDesktop ? fmtMoney(employeeSalesOverride.avgTips, sym) : fmtMoneyShort(employeeSalesOverride.avgTips, sym), shortValue: fmtMoneyShort(employeeSalesOverride.avgTips, sym) };
        }
        if (voidsOverride != null) {
            // Voided Items (the card value) is the removed-quantity count, distinct from
            // `totalVoids` (orders affected) which now surfaces only in the subtext — see
            // the matching split on Snapshot's void tile.
            if (k.key === "voids") updated = { ...updated, value: String(voidsOverride.itemsTotal), subtext: `${fmtMoney(voidsOverride.totalVoidAmount, sym)} · ${voidsOverride.totalVoids} ${voidsOverride.totalVoids === 1 ? 'order' : 'orders'}` };
        }
        if (comparisonOverride) {
            // DPFIX-06 — `/sales/labour/kpi-comparison` returns `null` for any delta it cannot
            // express as a percentage, and every null becomes 0 here, so the card reads
            // "0.00%" exactly as Snapshot and Sales do (their endpoint types these fields
            // `number` and answers 0 outright). Labor used to render null as "N/A", which made
            // the three reports describe the same flat period two different ways.
            //
            // Known and accepted: null also covers a KPI that grew out of nothing (0 → n,
            // where a percentage change is genuinely undefined), so that case now reads as no
            // change rather than as an uncomputable one. Called by the branch owner in favour
            // of one consistent reading across the three reports. If it ever needs separating
            // again, the payload carries its own current-period value beside each delta
            // (`totalWorkingHours`, `staffWorked`, `employeeSales`, `splh`, `totalTips`,
            // `voids`): a zero there means both periods were empty, a nonzero one means only
            // the prior period was.
            updated = { ...updated, delta: comparisonOverride[KPI_DELTA_FIELD[k.key]] ?? 0 };
        }
        return updated;
    });
    return (
        <div style={isDesktop
            ? { display: "flex", gap: 12, flexWrap: "nowrap" }
            : { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {kpis.map((k) => (
                <KPICard key={k.key} kpi={k} priorLabel={data.priorLabel} onTap={() => onDrill(k.key)} showDelta={showDelta} />
            ))}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STAFF PERFORMANCE TABLE
   ═══════════════════════════════════════════════════════════════════════════ */
function StaffPerformanceTable({ employees, multiDay, sym, serverPagination, summary, onSearch, searchTerm, buildExport, dateRangeLabel, loading, sortBy, sortDir, onSortChange }: { employees: EmployeeAgg[]; multiDay: boolean; sym: string; serverPagination?: ServerPagination; summary?: any; onSearch?: (v: string) => void; searchTerm?: string; buildExport?: (format?: any, opts?: { locationId?: string }) => Promise<{ headers: string[]; rows: (string | number | null | undefined)[][]; totalRow?: boolean }>; dateRangeLabel?: string; loading?: boolean; sortBy?: string; sortDir?: "asc" | "desc"; onSortChange?: (sortBy: string, sortDir: "asc" | "desc") => void }) {
    const dash = (n: number) => (n === 0 ? "—" : null);
    const columns: Col[] = [
        { key: "name", label: "Employee Name", sortKey: "employeeName", value: (r) => r.empName, render: (r) => <span style={{ color: T.accent, fontWeight: 600 }}>{r.empName}</span> },
        // ...(multiDay ? [{ key: "days", label: "Days", align: "right" as const, value: (r: EmployeeAgg) => r.daysWorked, render: (r: EmployeeAgg) => `${r.daysWorked}` }] : []),
        { key: "hours", label: "Total Hours", align: "right", sortKey: "totalHours", value: (r) => r.hours, render: (r) => fmtHours(r.hours), footer: () => summary ? fmtHoursStr(summary.totalHours) : fmtHours(employees.reduce((a, r) => a + r.hours, 0)) },
        { key: "break", label: "Break", align: "right", sortKey: "breakHours", value: (r) => r.breakHours, render: (r) => dash(r.breakHours) || fmtHours(r.breakHours), footer: () => summary ? fmtHoursStr(summary.totalBreakHours) : fmtHours(employees.reduce((a, r) => a + r.breakHours, 0)) },
        { key: "sales", label: "Sales", align: "right", sortKey: "sales", value: (r) => r.sales, render: (r) => fmtMoney(r.sales, sym), footer: () => fmtMoney(summary ? summary.totalSales : employees.reduce((a, r) => a + r.sales, 0), sym) },
        { key: "splh", label: "SPLH", align: "right", sortKey: "splh", value: (r) => r.splh ?? -1, render: (r) => (r.splh == null ? "—" : fmtMoney(r.splh, sym)), footer: () => summary?.totalSplh != null ? fmtMoney(summary.totalSplh, sym) : "—" },
        { key: "tips", label: "Tips", align: "right", sortKey: "tips", value: (r) => r.tips, render: (r) => fmtMoney(r.tips, sym), footer: () => fmtMoney(summary ? summary.totalTips : employees.reduce((a, r) => a + r.tips, 0), sym) },
        { key: "gratuity", label: "Gratuity", align: "right", sortKey: "gratuity", value: (r) => r.gratuity, render: (r) => dash(r.gratuity) || fmtMoney(r.gratuity, sym), footer: () => fmtMoney(summary ? summary.totalGratuity : employees.reduce((a, r) => a + r.gratuity, 0), sym) },
        { key: "voids", label: "Void", align: "right", sortKey: "voids", value: (r) => r.voidCount, render: (r) => `${r.voidCount}`, footer: () => `${summary ? summary.totalVoids : employees.reduce((a, r) => a + r.voidCount, 0)}` },
        { key: "voidAmt", label: "Void Amount", align: "right", sortKey: "voidAmount", value: (r) => r.voidAmount, render: (r) => (r.voidAmount === 0 ? <span style={{ color: T.t2 }}>—</span> : <span style={{ color: T.orange }}>{fmtMoney(r.voidAmount, sym)}</span>), footer: () => fmtMoney(summary ? summary.totalVoidAmount : employees.reduce((a, r) => a + r.voidAmount, 0), sym) },
    ];
    const mobileCard = (r: EmployeeAgg) => (
        <div style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ color: T.accent, fontWeight: 600, fontSize: 13 }}>{r.empName}</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: T.t1 }}>{fmtMoney(r.sales, sym)}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, fontSize: 11 }}>
                <MiniStat label="Hours" value={fmtHours(r.hours)} />
                <MiniStat label="Break" value={r.breakHours === 0 ? "—" : fmtHours(r.breakHours)} />
                <MiniStat label="SPLH" value={r.splh == null ? "—" : fmtMoney(r.splh, sym)} />
                <MiniStat label="Tips" value={fmtMoney(r.tips, sym)} />
                <MiniStat label="Gratuity" value={r.gratuity === 0 ? "—" : fmtMoney(r.gratuity, sym)} />
                <MiniStat label="Void Amount" value={r.voidAmount === 0 ? "—" : fmtMoney(r.voidAmount, sym)} color={r.voidAmount > 0 ? T.orange : undefined} />
            </div>
        </div>
    );
    return (
        <DataTableCard
            id="labor-staff-performance"
            title="Staff Performance"
            subtitle="Employee productivity and attendance for the selected period."
            columns={columns}
            rows={employees}
            defaultCompare={(a: EmployeeAgg, b: EmployeeAgg) => b.sales - a.sales}
            gaTable="staff_performance"
            onTrack={track}
            searchValue={(r: EmployeeAgg) => r.empName}
            mobileCard={mobileCard}
            showFooter
            exportConfig={{
                title: "Staff Performance",
                filename: `Labor_StaffPerformance_${isoDate(new Date())}`,
                dateRangeLabel,
                buildExport: buildExport ?? (() => ({
                    headers: ["Employee Name", "Total Hours", "Break Hours", "Sales", "SPLH", "Tips", "Gratuity", "Void", "Void Amount"],
                    rows: [
                        ...employees.map((e: EmployeeAgg) => [
                            e.empName, fmtHours(e.hours), fmtHours(e.breakHours), formatMoneyForExport(e.sales),
                            e.splh == null ? "—" : formatMoneyForExport(e.splh), formatMoneyForExport(e.tips), formatMoneyForExport(e.gratuity), e.voidCount, formatMoneyForExport(e.voidAmount),
                        ]),
                        buildStaffPerformanceTotalRow(employees, summary),
                    ],
                    totalRow: true,
                })),
            }}
            serverPagination={serverPagination}
            onSearch={onSearch}
            searchTerm={searchTerm}
            loading={loading}
            serverSort={sortBy && sortDir && onSortChange ? { sortBy, sortDir, onSortChange } : undefined}
        />
    );
}

function MiniStat({ label, value, color }: any) {
    return (
        <div>
            <div style={{ color: T.t3, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
            <div style={{ color: color || T.t1, fontWeight: 600, fontSize: 11, marginTop: 1 }}>{value}</div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   WORKING HOURS LOG
   ═══════════════════════════════════════════════════════════════════════════ */
function WorkingHoursLog({ shifts, serverPagination, onSearch, searchTerm, buildExport, dateRangeLabel, loading, sortBy, sortDir, onSortChange }: { shifts: LaborShift[]; serverPagination?: ServerPagination; onSearch?: (v: string) => void; searchTerm?: string; buildExport?: (format?: any, opts?: { locationId?: string }) => Promise<{ headers: string[]; rows: (string | number | null | undefined)[][] }>; dateRangeLabel?: string; loading?: boolean; sortBy?: string; sortDir?: "asc" | "desc"; onSortChange?: (sortBy: string, sortDir: "asc" | "desc") => void }) {
    const breakCell = (s: LaborShift) => {
        if (s.breakMinutes < 0) {
            return <span style={{ color: T.red, fontWeight: 600 }}>⚠ {fmtHours(s.breakMinutes / 60)}</span>;
        }
        if (s.breakMinutes === 0) return <span style={{ color: T.t2 }}>—</span>;
        const longBreak = s.hours > 0 && s.breakMinutes / 60 > s.hours * 0.5;
        return <span style={{ color: longBreak ? T.orange : T.t1, fontWeight: longBreak ? 600 : 400 }}>{fmtHours(s.breakMinutes / 60)}</span>;
    };
    const workedCell = (s: LaborShift) => {
        if (s.anomalyZero) return <span style={{ color: T.orange, fontWeight: 600 }}>⚠ -</span>;
        return fmtHours(s.hours);
    };
    const columns: Col[] = [
        { key: "date", label: "Date", sortKey: "date", value: (r) => `${r.date} ${clockToNum(r.clockIn)}`, render: (r) => fmtShiftDate(r.date) },
        { key: "name", label: "Employee Name", sortKey: "employeeName", value: (r) => r.empName, render: (r) => <span style={{ color: T.accent, fontWeight: 600 }}>{r.empName}</span> },
        { key: "in", label: "Clock-In", sortKey: "clockIn", value: (r) => clockToNum(r.clockIn), render: (r) => fmtClock(r.clockIn) },
        { key: "out", label: "Clock-Out", sortKey: "clockOut", value: (r) => clockToNum(r.clockOut), render: (r) => (r.clockOut ? fmtClock(r.clockOut) : <span style={{ color: T.green, fontStyle: "italic" }}>In Progress</span>) },
        { key: "worked", label: "Worked Hours", align: "right", sortKey: "workedHours", value: (r) => r.hours, render: workedCell },
        { key: "break", label: "Break Hours", align: "right", sortKey: "breakHours", value: (r) => r.breakMinutes, render: breakCell },
    ];
    const defaultCompare = (a: LaborShift, b: LaborShift) => {
        if (a.date !== b.date) return a.date < b.date ? 1 : -1;
        return clockToNum(b.clockIn) - clockToNum(a.clockIn);
    };
    const mobileCard = (s: LaborShift) => (
        <div style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.t1 }}>{fmtShiftDate(s.date)}</span>
                <span style={{ color: T.accent, fontWeight: 600, fontSize: 12 }}>{s.empName}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11 }}>
                <MiniStat label="Clock-In" value={fmtClock(s.clockIn)} />
                <MiniStat label="Clock-Out" value={s.clockOut ? fmtClock(s.clockOut) : "In Progress"} color={s.clockOut ? undefined : T.green} />
                <MiniStat label="Worked" value={s.anomalyZero ? `⚠ -` : fmtHours(s.hours)} color={s.anomalyZero ? T.orange : undefined} />
                <MiniStat label="Break" value={s.breakMinutes < 0 ? `⚠ ${fmtHours(s.breakMinutes / 60)}` : s.breakMinutes === 0 ? "—" : fmtHours(s.breakMinutes / 60)} color={s.breakMinutes < 0 ? T.red : undefined} />
            </div>
        </div>
    );
    return (
        <DataTableCard
            id="labor-working-hours"
            title="Working Hours Log"
            subtitle="Individual shift records for the selected period."
            columns={columns}
            rows={shifts}
            defaultCompare={defaultCompare}
            gaTable="working_hours_log"
            onTrack={track}
            searchValue={(r: LaborShift) => r.empName}
            mobileCard={mobileCard}
            exportConfig={{
                title: "Working Hours Log",
                filename: `Labor_WorkingHoursLog_${isoDate(new Date())}`,
                dateRangeLabel,
                buildExport: buildExport ?? (() => ({
                    headers: ["Date", "Employee Name", "Clock-In", "Clock-Out", "Worked Hours", "Break Hours"],
                    rows: shifts.map((s: LaborShift) => [
                        fmtShiftDate(s.date), s.empName, fmtClock(s.clockIn),
                        s.clockOut ? fmtClock(s.clockOut) : "In Progress",
                        fmtHours(s.hours), fmtHours(s.breakMinutes / 60),
                    ]),
                })),
            }}
            serverPagination={serverPagination}
            onSearch={onSearch}
            searchTerm={searchTerm}
            loading={loading}
            serverSort={sortBy && sortDir && onSortChange ? { sortBy, sortDir, onSortChange } : undefined}
        />
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL-IN VIEWS
   ═══════════════════════════════════════════════════════════════════════════ */
function DrillHours({ sym: _sym }: { data: LaborData; sym: string }) {
    const apiSummary = useAppSelector((s) => s.clickHouse.labourWorkingHoursSummary);
    const apiItems = useAppSelector((s) => s.clickHouse.labourWorkingHoursItems);

    const totalBreakHours = fmtHoursStr(apiSummary?.totalBreakHours);
    const netProductiveHours = fmtHoursStr(apiSummary?.netProductiveHours);
    const avgHoursPerEmployee = fmtHoursStr(apiSummary?.avgHoursPerEmployee);

    const sorted = [...apiItems].sort((a, b) => parseHoursStr(b.workingHours) - parseHoursStr(a.workingHours));
    const max = sorted.length ? parseHoursStr(sorted[0].workingHours) : 1;

    return (
        <div>
            <MiniKpiRow items={[
                { label: "Total Break Hours", value: totalBreakHours },
                { label: "Total Working Hours", value: netProductiveHours },
                { label: "Avg Hours / Employee", value: avgHoursPerEmployee },
            ]} />
            <SectionCard title="Hours by Employee" subtitle="Ranked by total hours worked">
                {sorted.length === 0
                    ? <div style={{ color: T.t3, fontSize: 13, padding: 16, textAlign: "center" }}>No hours data for this period.</div>
                    : sorted.map((item) => (
                        <RankedRow key={item.employeeName} name={item.employeeName} value={fmtHoursStr(item.workingHours)}
                            secondary={`(${fmtHoursStr(item.breakHours)} break)`} pct={max > 0 ? (parseHoursStr(item.workingHours) / max) * 100 : 0} color={`${T.green}99`} />
                    ))}
            </SectionCard>
        </div>
    );
}

function DrillSales({ sym }: { data: LaborData; sym: string }) {
    const apiSummary = useAppSelector((s) => s.clickHouse.labourEmployeeSalesSummary);
    const apiItems = useAppSelector((s) => s.clickHouse.labourEmployeeSalesItems);

    const totalSales = apiSummary?.totalSales ?? 0;
    const avgSales = apiSummary?.avgSalesPerEmployee ?? 0;
    const topSeller = apiSummary?.topSeller ?? '—';

    const sorted = [...apiItems].sort((a, b) => b.sales - a.sales);
    const max = sorted.length ? sorted[0].sales : 1;

    return (
        <div>
            <MiniKpiRow items={[
                { label: "Total Sales", value: fmtMoney(totalSales, sym), tooltip: "Net Sales which excludes taxes and all deductions" },
                { label: "Avg Sales / Emp", value: fmtMoney(avgSales, sym) },
                { label: "Top Seller", value: topSeller, color: T.accent },
            ]} />
            <SectionCard title="Sales by Employee" subtitle="Ranked by attributed item sales">
                {sorted.length === 0
                    ? <div style={{ color: T.t3, fontSize: 13, padding: 16, textAlign: "center" }}>No sales data for this period.</div>
                    : sorted.map((item, i) => (
                        <RankedRow key={item.employeeName} rank={i + 1} name={item.employeeName} value={fmtMoney(item.sales, sym)}
                            secondary={`${item.orderCount} orders · ${fmtMoney(item.tips, sym)} tips`}
                            pct={max > 0 ? (item.sales / max) * 100 : 0} color={`${T.accent}99`} />
                    ))}
            </SectionCard>
        </div>
    );
}

function DrillTips({ sym }: { data: LaborData; sym: string }) {
    const apiSummary = useAppSelector((s) => s.clickHouse.labourTipsSummary);
    const apiItems = useAppSelector((s) => s.clickHouse.labourTipsItems);

    const totalTips = apiSummary?.totalTips ?? 0;
    const avgTips = apiSummary?.avgTipsPerEmployee ?? 0;
    const tipRate = apiSummary?.tipRate ?? 0;

    const sorted = [...apiItems].sort((a, b) => b.tips - a.tips);
    const max = sorted.length ? sorted[0].tips : 1;

    return (
        <div>
            <MiniKpiRow items={[
                { label: "Total Tips", value: fmtMoney(totalTips, sym) },
                { label: "Avg Tips / Emp", value: fmtMoney(avgTips, sym) },
                { label: "Tip Rate", value: tipRate != null ? `${Number(tipRate).toFixed(1)}%` : '—', tooltip: "Provides the tips to employee sales value. Calculated by (Tips/ employee sales) x 100" },
            ]} />
            <SectionCard title="Tips by Employee" subtitle="Ranked by tip amount">
                {sorted.length === 0
                    ? <div style={{ color: T.t3, fontSize: 13, padding: 16, textAlign: "center" }}>No tips data for this period.</div>
                    : sorted.map((item) => (
                        <RankedRow key={item.employeeName} name={item.employeeName} value={fmtMoney(item.tips, sym)}
                            secondary={`${Number(item.tipRate ?? 0).toFixed(1)}% tip rate`}
                            pct={max > 0 ? (item.tips / max) * 100 : 0} color={`${T.green}99`} />
                    ))}
            </SectionCard>
        </div>
    );
}

function DrillVoids({ sym }: { data: LaborData; sym: string }) {
    const apiData = useAppSelector((s) => s.clickHouse.labourVoidsData);
    const isDesktop = useIsDesktop();

    const totalVoids = apiData?.totalVoids ?? 0;
    const totalVoidAmount = apiData?.totalVoidAmount ?? 0;
    const avgVoidValue = apiData?.avgVoidValue ?? 0;
    const reasons = apiData?.reasons ?? [];
    const byEmployee = apiData?.byEmployee ?? [];

    const sortedReasons = [...reasons].sort((a, b) => b.orderCount - a.orderCount);
    const maxReason = sortedReasons.length ? sortedReasons[0].orderCount : 1;

    const sortedEmployees = [...byEmployee].sort((a, b) => b.voidCount - a.voidCount);
    const maxEmployee = sortedEmployees.length ? sortedEmployees[0].voidCount : 1;

    return (
        <div>
            <MiniKpiRow items={[
                { label: "Total Voids", value: String(totalVoids) },
                { label: "Total Void Amount", value: fmtMoney(totalVoidAmount, sym), color: T.orange },
                { label: "Avg Void Value", value: fmtMoney(avgVoidValue, sym) },
            ]} />
            <SectionCard title="Void Reasons" subtitle="Frequency by reason">
                {sortedReasons.length === 0
                    ? <div style={{ color: T.t3, fontSize: 13, padding: 16, textAlign: "center" }}>No void reasons for this period.</div>
                    : sortedReasons.map((item) => (
                        <RankedRow key={item.cancelReason || "no-reason"} name={item.cancelReason || "No Reason Given"}
                            value={`${item.orderCount}x`}
                            pct={maxReason > 0 ? (item.orderCount / maxReason) * 100 : 0}
                            color={`${T.orange}99`}
                            dimmed={!item.cancelReason} />
                    ))
                }
            </SectionCard>
            <div style={{ marginTop: 16 }} />
            <SectionCard title="Voids by Employee" subtitle="Ranked by void count">
                {sortedEmployees.length === 0
                    ? <div style={{ color: T.t3, fontSize: 13, padding: 16, textAlign: "center" }}>No voids recorded for this period.</div>
                    : sortedEmployees.map((item) => (
                        <div key={item.employeeName} style={{ padding: isDesktop ? "10px 0" : "8px 0", borderBottom: `1px solid ${T.divider}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
                                <span style={{ fontSize: isDesktop ? 13 : 12, fontWeight: 600, color: T.accent }}>{item.employeeName}</span>
                                <span style={{ fontSize: isDesktop ? 13 : 12, fontWeight: 700, color: T.t1 }}>{item.voidCount}</span>
                            </div>
                            <div style={{ height: isDesktop ? 6 : 5, background: "var(--hover)", borderRadius: 3, overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${maxEmployee > 0 ? (item.voidCount / maxEmployee) * 100 : 0}%`, background: `${T.orange}99`, borderRadius: 3 }} />
                            </div>
                            <div style={{ fontSize: isDesktop ? 11 : 10, color: "rgba(217, 119, 6, 0.85)", marginTop: 4 }}>{fmtMoney(item.voidAmount, sym)}</div>
                        </div>
                    ))
                }
            </SectionCard>
        </div>
    );
}

const DRILL_MAP: Record<string, any> = { hours: DrillHours, sales: DrillSales, tips: DrillTips, voids: DrillVoids };

/* ═══════════════════════════════════════════════════════════════════════════
   SHIMMERS
   ═══════════════════════════════════════════════════════════════════════════ */
function DesktopShimmer() {
    return (
        <div className="rpt-page">
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} style={{ flex: 1, minWidth: 180, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 20, boxShadow: T.cardShadow }}>
                        <Bone h={12} w={90} mb={12} /><Bone h={28} w={110} mb={8} /><Bone h={12} w={70} />
                    </div>
                ))}
            </div>
            {[0, 1].map((i) => (
                <div key={i} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow }}>
                    <Bone h={16} w={180} mb={16} />
                    {Array.from({ length: 6 }).map((_, r) => <Bone key={r} h={14} mb={10} />)}
                </div>
            ))}
        </div>
    );
}
function MobileShimmer() {
    return (
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 88px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: "12px 10px", boxShadow: T.shadow }}>
                        <Bone h={8} w={50} mb={6} /><Bone h={18} />
                    </div>
                ))}
            </div>
            {[0, 1].map((i) => (
                <div key={i} style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 14, marginBottom: 8, boxShadow: T.shadow }}>
                    <Bone h={13} w={140} mb={12} />
                    {Array.from({ length: 4 }).map((_, r) => <Bone key={r} h={40} r={10} mb={8} />)}
                </div>
            ))}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DATA HOOK
   ═══════════════════════════════════════════════════════════════════════════ */
function useLaborData(period: string, sym: string) {
    const data = useMemo(() => buildLaborData(period, null, sym), [period, sym]);
    return { data };
}


/* ═══════════════════════════════════════════════════════════════════════════
   DESKTOP
   ═══════════════════════════════════════════════════════════════════════════ */
function LaborDesktop({ period, setPeriod, employeeId, setEmployeeId, options, locationId, dateRange }: any) {
    const sym = useCurrencySymbol();
    const { data } = useLaborData(period, sym);
    const dispatch = useAppDispatch();
    const workingHoursSummary = useAppSelector((s) => s.clickHouse.labourWorkingHoursSummary);
    const employeeSalesSummary = useAppSelector((s) => s.clickHouse.labourEmployeeSalesSummary);
    const labourTipsSummary = useAppSelector((s) => s.clickHouse.labourTipsSummary);
    const labourVoidsData = useAppSelector((s) => s.clickHouse.labourVoidsData);
    const labourKpiComparison = useAppSelector((s) => s.clickHouse.labourKpiComparison);
    const apiStaffItems = useAppSelector((s) => s.clickHouse.labourStaffPerformanceItems);
    const staffTotalElements = useAppSelector((s) => s.clickHouse.labourStaffPerformanceTotalElements);
    const staffSummary = useAppSelector((s) => s.clickHouse.labourStaffPerformanceSummary);
    const apiShiftItems = useAppSelector((s) => s.clickHouse.labourWorkingHoursLogItems);
    const hoursTotalElements = useAppSelector((s) => s.clickHouse.labourWorkingHoursLogTotalElements);
    const apiLoading = useAppSelector((s) => s.clickHouse.labourEmployeeSalesLoading || s.clickHouse.labourTipsLoading || s.clickHouse.labourVoidsLoading || s.clickHouse.labourKpiComparisonLoading || s.clickHouse.labourWorkingHoursLoading);
    const staffPerformanceLoading = useAppSelector((s) => s.clickHouse.labourStaffPerformanceLoading);
    const workingHoursLogLoading = useAppSelector((s) => s.clickHouse.labourWorkingHoursLogLoading);
    const setTopBar = useDesktopTopBar();
    const [drill, setDrill] = useState<string | null>(null);
    const [staffPage, setStaffPage] = useState(1);
    const [staffPageSize, setStaffPageSize] = useState(10);
    const [staffSearch, setStaffSearch] = useState("");
    const [debouncedStaffSearch, setDebouncedStaffSearch] = useState("");
    const [staffSortBy, setStaffSortBy] = useState("sales");
    const [staffSortDir, setStaffSortDir] = useState<"asc" | "desc">("desc");
    const [hoursPage, setHoursPage] = useState(1);
    const [hoursPageSize, setHoursPageSize] = useState(10);
    const [hoursSearch, setHoursSearch] = useState("");
    const [debouncedHoursSearch, setDebouncedHoursSearch] = useState("");
    const [hoursSortBy, setHoursSortBy] = useState("date");
    const [hoursSortDir, setHoursSortDir] = useState<"asc" | "desc">("desc");
    const doDrill = useCallback((k: string) => { if (!DRILL_MAP[k]) return; track("maghil_reports_drill_open", { drill_target: k, drill_depth: 1 }); setDrill(k); window.scrollTo({ top: 0 }); }, []);
    const goBack = useCallback(() => { if (drill) track("maghil_reports_drill_close", { drill_target: drill }); setDrill(null); }, [drill]);
    const handleStaffSortChange = useCallback((sortBy: string, sortDir: "asc" | "desc") => { setStaffSortBy(sortBy); setStaffSortDir(sortDir); setStaffPage(1); }, []);
    const handleHoursSortChange = useCallback((sortBy: string, sortDir: "asc" | "desc") => { setHoursSortBy(sortBy); setHoursSortDir(sortDir); setHoursPage(1); }, []);

    const isToday = String(period).trim().toLowerCase() === "today";
    const exportDateRangeLabel = exportPeriodFor(period, dateRange)?.label ?? period;

    // Ref guards — prevent React StrictMode double-invoke from firing duplicate API calls
    const lastEmployeeSalesKey = useRef("");
    const lastTipsKey = useRef("");
    const lastVoidsKey = useRef("");
    const lastKpiComparisonKey = useRef("");
    const lastWorkingHoursKey = useRef("");
    const lastStaffKey = useRef("");
    const lastHoursLogKey = useRef("");

    // Debounce search inputs
    useEffect(() => { const t = setTimeout(() => { setDebouncedStaffSearch(staffSearch); setStaffPage(1); }, 400); return () => clearTimeout(t); }, [staffSearch]);
    useEffect(() => { const t = setTimeout(() => { setDebouncedHoursSearch(hoursSearch); setHoursPage(1); }, 400); return () => clearTimeout(t); }, [hoursSearch]);

    // Reset to page 1 and clear search when date range changes
    useEffect(() => { setStaffPage(1); setHoursPage(1); setStaffSearch(""); setHoursSearch(""); setDebouncedStaffSearch(""); setDebouncedHoursSearch(""); }, [dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastEmployeeSalesKey.current) return;
        lastEmployeeSalesKey.current = key;
        dispatch(fetchLabourEmployeeSalesRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastTipsKey.current) return;
        lastTipsKey.current = key;
        dispatch(fetchLabourTipsRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastVoidsKey.current) return;
        lastVoidsKey.current = key;
        dispatch(fetchLabourVoidsRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastKpiComparisonKey.current) return;
        lastKpiComparisonKey.current = key;
        dispatch(fetchLabourKpiComparisonRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, isToday, period, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastWorkingHoursKey.current) return;
        lastWorkingHoursKey.current = key;
        dispatch(fetchLabourWorkingHoursRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${staffPage}|${staffPageSize}|${debouncedStaffSearch}|${staffSortBy}|${staffSortDir}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastStaffKey.current) return;
        lastStaffKey.current = key;
        dispatch(fetchLabourStaffPerformanceRequest({ locationId, page: staffPage, size: staffPageSize, search: debouncedStaffSearch, sortBy: staffSortBy, sortAsc: staffSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, staffPage, staffPageSize, debouncedStaffSearch, staffSortBy, staffSortDir, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${hoursPage}|${hoursPageSize}|${debouncedHoursSearch}|${hoursSortBy}|${hoursSortDir}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastHoursLogKey.current) return;
        lastHoursLogKey.current = key;
        dispatch(fetchLabourWorkingHoursLogRequest({ locationId, page: hoursPage, size: hoursPageSize, search: debouncedHoursSearch, sortBy: hoursSortBy, sortAsc: hoursSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, hoursPage, hoursPageSize, debouncedHoursSearch, hoursSortBy, hoursSortDir, dateRange?.startDate, dateRange?.endDate]);

    const employees = useMemo(() => mapStaffPerformance(apiStaffItems), [apiStaffItems]);
    const shifts = useMemo(() => mapWorkingHoursLog(apiShiftItems), [apiShiftItems]);

    useEffect(() => {
        setTopBar({
            breadcrumbs: drill ? "Team › Labor" : "Team",
            title: drill ? DRILL_TITLES[drill] : "Labor",
            dateFilters: drill ? undefined : { value: period, onChange: (v: string) => { setPeriod(v); track("maghil_reports_filter_change", { filter_type: "date_range", filter_value: v }); } },
            extraContent: drill ? null : (
                <EmployeeFilter options={options} value={employeeId} onChange={setEmployeeId} />
            ),
            leftAddon: drill ? (
                <div onClick={goBack} style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginRight: 4 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
                </div>
            ) : null,
        });
    }, [setTopBar, drill, period, employeeId, options, data, sym]);

    const isLoading = apiLoading;

    const workingHoursOverride = workingHoursSummary
        ? { totalHours: fmtHoursStr(workingHoursSummary.netProductiveHours) }
        : null;

    const employeeSalesOverride = (employeeSalesSummary || labourTipsSummary)
        ? { avgSales: employeeSalesSummary?.totalSales ?? null, avgTips: labourTipsSummary?.totalTips ?? null }
        : null;

    const voidsOverride = labourVoidsData
        ? {
            totalVoids: labourVoidsData.totalVoids,
            totalVoidAmount: labourVoidsData.totalVoidAmount,
            // Voided Items = sum of removed quantity across reasons (falls back to the
            // order-count total if a reason is missing removedQty, e.g. legacy rows).
            itemsTotal: labourVoidsData.reasons.reduce((s, r) => s + (r.removedQty ?? r.orderCount ?? 0), 0),
        }
        : null;

    let body: React.ReactNode;
    if (isLoading) body = <DesktopShimmer />;
    else if (drill) { const D = DRILL_MAP[drill]; body = <D data={data} sym={sym} />; }
    else body = (
        <>
            <KpiGrid data={data} onDrill={doDrill} workingHoursOverride={workingHoursOverride} staffCountOverride={labourKpiComparison?.staffWorked ?? null} splhOverride={labourKpiComparison?.splh ?? null} employeeSalesOverride={employeeSalesOverride} voidsOverride={voidsOverride} comparisonOverride={labourKpiComparison} showDelta={!!labourKpiComparison && allowsComparison(period)} />
            <StaffPerformanceTable
                dateRangeLabel={exportDateRangeLabel}
                employees={employees}
                multiDay={data.multiDay}
                sym={sym}
                summary={staffSummary}
                onSearch={(v: string) => setStaffSearch(v)}
                searchTerm={staffSearch}
                loading={staffPerformanceLoading}
                serverPagination={{ totalElements: staffTotalElements, page: staffPage, pageSize: staffPageSize, onPageChange: (p, s) => { setStaffPage(p); setStaffPageSize(s); } }}
                sortBy={staffSortBy}
                sortDir={staffSortDir}
                onSortChange={handleStaffSortChange}
                buildExport={async (_format, opts) => {
                    const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
                    const eid = employeeId ?? "";
                    const targetLocationId = opts?.locationId ?? locationId;
                    let exportSize = staffTotalElements || 1000;
                    if (opts?.locationId) {
                        const probe: any = await getLabourStaffPerformanceApi({ locationId: targetLocationId, page: 1, size: 1, search: debouncedStaffSearch, sortBy: staffSortBy, sortAsc: staffSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) });
                        exportSize = Number(probe?.totalElements) || 1000;
                    }
                    const res: any = await getLabourStaffPerformanceApi({ locationId: targetLocationId, page: 1, size: exportSize, search: debouncedStaffSearch, sortBy: staffSortBy, sortAsc: staffSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) });
                    const list = mapStaffPerformance(Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []));
                    return {
                        headers: ["Employee Name", "Total Hours", "Break Hours", "Sales", "SPLH", "Tips", "Gratuity", "Void", "Void Amount"],
                        rows: [
                            ...list.map((e) => [e.empName, fmtHours(e.hours), fmtHours(e.breakHours), formatMoneyForExport(e.sales), e.splh == null ? "—" : formatMoneyForExport(e.splh), formatMoneyForExport(e.tips), formatMoneyForExport(e.gratuity), e.voidCount, formatMoneyForExport(e.voidAmount)]),
                            buildStaffPerformanceTotalRow(list, res?.summary),
                        ],
                        totalRow: true,
                    };
                }}
            />
            <WorkingHoursLog
                dateRangeLabel={exportDateRangeLabel}
                shifts={shifts}
                onSearch={(v: string) => setHoursSearch(v)}
                searchTerm={hoursSearch}
                loading={workingHoursLogLoading}
                serverPagination={{ totalElements: hoursTotalElements, page: hoursPage, pageSize: hoursPageSize, onPageChange: (p, s) => { setHoursPage(p); setHoursPageSize(s); } }}
                sortBy={hoursSortBy}
                sortDir={hoursSortDir}
                onSortChange={handleHoursSortChange}
                buildExport={async (_format, opts) => {
                    const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
                    const eid = employeeId ?? "";
                    const targetLocationId = opts?.locationId ?? locationId;
                    let exportSize = hoursTotalElements || 1000;
                    if (opts?.locationId) {
                        const probe: any = await getLabourWorkingHoursLogApi({ locationId: targetLocationId, page: 1, size: 1, search: debouncedHoursSearch, sortBy: hoursSortBy, sortAsc: hoursSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) });
                        exportSize = Number(probe?.totalElements) || 1000;
                    }
                    const res: any = await getLabourWorkingHoursLogApi({ locationId: targetLocationId, page: 1, size: exportSize, search: debouncedHoursSearch, sortBy: hoursSortBy, sortAsc: hoursSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) });
                    const list = mapWorkingHoursLog(Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []));
                    return {
                        headers: ["Date", "Employee Name", "Clock-In", "Clock-Out", "Worked Hours", "Break Hours"],
                        rows: list.map((s) => [fmtShiftDate(s.date), s.empName, fmtClock(s.clockIn), s.clockOut ? fmtClock(s.clockOut) : "In Progress", fmtHours(s.hours), fmtHours(s.breakMinutes / 60)]),
                    };
                }}
            />
        </>
    );

    return (
        <DesktopLayout>
            <div className="rpt-page">{body}</div>
        </DesktopLayout>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE
   ═══════════════════════════════════════════════════════════════════════════ */
function LaborMobile({ period, setPeriod, employeeId, setEmployeeId, options, locationId, dateRange }: any) {
    const sym = useCurrencySymbol();
    const { data } = useLaborData(period, sym);
    const dispatch = useAppDispatch();
    const workingHoursSummary = useAppSelector((s) => s.clickHouse.labourWorkingHoursSummary);
    const employeeSalesSummary = useAppSelector((s) => s.clickHouse.labourEmployeeSalesSummary);
    const labourTipsSummary = useAppSelector((s) => s.clickHouse.labourTipsSummary);
    const labourVoidsData = useAppSelector((s) => s.clickHouse.labourVoidsData);
    const labourKpiComparison = useAppSelector((s) => s.clickHouse.labourKpiComparison);
    const apiStaffItems = useAppSelector((s) => s.clickHouse.labourStaffPerformanceItems);
    const staffTotalElements = useAppSelector((s) => s.clickHouse.labourStaffPerformanceTotalElements);
    const staffSummary = useAppSelector((s) => s.clickHouse.labourStaffPerformanceSummary);
    const apiShiftItems = useAppSelector((s) => s.clickHouse.labourWorkingHoursLogItems);
    const hoursTotalElements = useAppSelector((s) => s.clickHouse.labourWorkingHoursLogTotalElements);
    const apiLoading = useAppSelector((s) => s.clickHouse.labourEmployeeSalesLoading || s.clickHouse.labourTipsLoading || s.clickHouse.labourVoidsLoading || s.clickHouse.labourKpiComparisonLoading || s.clickHouse.labourWorkingHoursLoading);
    const staffPerformanceLoading = useAppSelector((s) => s.clickHouse.labourStaffPerformanceLoading);
    const workingHoursLogLoading = useAppSelector((s) => s.clickHouse.labourWorkingHoursLogLoading);
    const setTopBar = useDesktopTopBar();
    const [drill, setDrill] = useState<string | null>(null);
    const isDrill = drill != null;
    const [staffPage, setStaffPage] = useState(1);
    const [staffPageSize, setStaffPageSize] = useState(10);
    const [staffSearch, setStaffSearch] = useState("");
    const [debouncedStaffSearch, setDebouncedStaffSearch] = useState("");
    const [staffSortBy, setStaffSortBy] = useState("sales");
    const [staffSortDir, setStaffSortDir] = useState<"asc" | "desc">("desc");
    const [hoursPage, setHoursPage] = useState(1);
    const [hoursPageSize, setHoursPageSize] = useState(10);
    const [hoursSearch, setHoursSearch] = useState("");
    const [debouncedHoursSearch, setDebouncedHoursSearch] = useState("");
    const [hoursSortBy, setHoursSortBy] = useState("date");
    const [hoursSortDir, setHoursSortDir] = useState<"asc" | "desc">("desc");
    useDrillHistoryBack(isDrill, () => setDrill(null));
    useSwipeBack();
    const doDrill = (k: string) => { if (!DRILL_MAP[k]) return; track("maghil_reports_drill_open", { drill_target: k, drill_depth: 1 }); setDrill(k); window.scrollTo({ top: 0 }); };
    const handleStaffSortChange = (sortBy: string, sortDir: "asc" | "desc") => { setStaffSortBy(sortBy); setStaffSortDir(sortDir); setStaffPage(1); };
    const handleHoursSortChange = (sortBy: string, sortDir: "asc" | "desc") => { setHoursSortBy(sortBy); setHoursSortDir(sortDir); setHoursPage(1); };

    const isToday = String(period).trim().toLowerCase() === "today";
    const exportDateRangeLabel = exportPeriodFor(period, dateRange)?.label ?? period;

    // Ref guards — prevent React StrictMode double-invoke from firing duplicate API calls
    const lastEmployeeSalesKey = useRef("");
    const lastTipsKey = useRef("");
    const lastVoidsKey = useRef("");
    const lastKpiComparisonKey = useRef("");
    const lastWorkingHoursKey = useRef("");
    const lastStaffKey = useRef("");
    const lastHoursLogKey = useRef("");

    // Debounce search inputs
    useEffect(() => { const t = setTimeout(() => { setDebouncedStaffSearch(staffSearch); setStaffPage(1); }, 400); return () => clearTimeout(t); }, [staffSearch]);
    useEffect(() => { const t = setTimeout(() => { setDebouncedHoursSearch(hoursSearch); setHoursPage(1); }, 400); return () => clearTimeout(t); }, [hoursSearch]);

    // Reset to page 1 and clear search when date range changes
    useEffect(() => { setStaffPage(1); setHoursPage(1); setStaffSearch(""); setHoursSearch(""); setDebouncedStaffSearch(""); setDebouncedHoursSearch(""); }, [dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastEmployeeSalesKey.current) return;
        lastEmployeeSalesKey.current = key;
        dispatch(fetchLabourEmployeeSalesRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastTipsKey.current) return;
        lastTipsKey.current = key;
        dispatch(fetchLabourTipsRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastVoidsKey.current) return;
        lastVoidsKey.current = key;
        dispatch(fetchLabourVoidsRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastKpiComparisonKey.current) return;
        lastKpiComparisonKey.current = key;
        dispatch(fetchLabourKpiComparisonRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, isToday, period, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastWorkingHoursKey.current) return;
        lastWorkingHoursKey.current = key;
        dispatch(fetchLabourWorkingHoursRequest({ locationId, ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${staffPage}|${staffPageSize}|${debouncedStaffSearch}|${staffSortBy}|${staffSortDir}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastStaffKey.current) return;
        lastStaffKey.current = key;
        dispatch(fetchLabourStaffPerformanceRequest({ locationId, page: staffPage, size: staffPageSize, search: debouncedStaffSearch, sortBy: staffSortBy, sortAsc: staffSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, staffPage, staffPageSize, debouncedStaffSearch, staffSortBy, staffSortDir, dateRange?.startDate, dateRange?.endDate]);

    useEffect(() => {
        if (!locationId || !dateRange) return;
        const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const eid = employeeId ?? "";
        const key = `${locationId}|${hoursPage}|${hoursPageSize}|${debouncedHoursSearch}|${hoursSortBy}|${hoursSortDir}|${dateParams.startDate ?? ""}|${dateParams.endDate ?? ""}|${eid}`;
        if (key === lastHoursLogKey.current) return;
        lastHoursLogKey.current = key;
        dispatch(fetchLabourWorkingHoursLogRequest({ locationId, page: hoursPage, size: hoursPageSize, search: debouncedHoursSearch, sortBy: hoursSortBy, sortAsc: hoursSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) }));
    }, [dispatch, locationId, employeeId, hoursPage, hoursPageSize, debouncedHoursSearch, hoursSortBy, hoursSortDir, dateRange?.startDate, dateRange?.endDate]);

    const employees = useMemo(() => mapStaffPerformance(apiStaffItems), [apiStaffItems]);
    const shifts = useMemo(() => mapWorkingHoursLog(apiShiftItems), [apiShiftItems]);

    useEffect(() => {
        setTopBar({
            title: isDrill ? (drill === "voids" ? "Void" : DRILL_TITLES[drill!]) : "Labor",
            dateFilters: isDrill ? undefined : { value: period, onChange: (v: string) => { setPeriod(v); track("maghil_reports_filter_change", { filter_type: "date_range", filter_value: v }); } },
            onBack: isDrill ? () => setDrill(null) : undefined,
        });
    }, [setTopBar, isDrill, drill, period]);

    const isLoading = apiLoading;

    const workingHoursOverride = workingHoursSummary
        ? { totalHours: fmtHoursStr(workingHoursSummary.netProductiveHours) }
        : null;

    const employeeSalesOverride = (employeeSalesSummary || labourTipsSummary)
        ? { avgSales: employeeSalesSummary?.totalSales ?? null, avgTips: labourTipsSummary?.totalTips ?? null }
        : null;

    const voidsOverride = labourVoidsData
        ? {
            totalVoids: labourVoidsData.totalVoids,
            totalVoidAmount: labourVoidsData.totalVoidAmount,
            // Voided Items = sum of removed quantity across reasons (falls back to the
            // order-count total if a reason is missing removedQty, e.g. legacy rows).
            itemsTotal: labourVoidsData.reasons.reduce((s, r) => s + (r.removedQty ?? r.orderCount ?? 0), 0),
        }
        : null;

    let body: React.ReactNode;
    if (isLoading) body = <MobileShimmer />;
    else if (isDrill) { const D = DRILL_MAP[drill!]; body = <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 88px" }}><D data={data} sym={sym} /></div>; }
    else body = (
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 88px", display: "flex", flexDirection: "column", gap: 12 }}>
            <EmployeeFilter options={options} value={employeeId} onChange={setEmployeeId} compact />
            <KpiGrid data={data} onDrill={doDrill} workingHoursOverride={workingHoursOverride} staffCountOverride={labourKpiComparison?.staffWorked ?? null} splhOverride={labourKpiComparison?.splh ?? null} employeeSalesOverride={employeeSalesOverride} voidsOverride={voidsOverride} comparisonOverride={labourKpiComparison} showDelta={!!labourKpiComparison && allowsComparison(period)} />
            <StaffPerformanceTable
                dateRangeLabel={exportDateRangeLabel}
                employees={employees}
                multiDay={data.multiDay}
                sym={sym}
                summary={staffSummary}
                onSearch={(v: string) => setStaffSearch(v)}
                searchTerm={staffSearch}
                loading={staffPerformanceLoading}
                serverPagination={{ totalElements: staffTotalElements, page: staffPage, pageSize: staffPageSize, onPageChange: (p, s) => { setStaffPage(p); setStaffPageSize(s); } }}
                sortBy={staffSortBy}
                sortDir={staffSortDir}
                onSortChange={handleStaffSortChange}
                buildExport={async (_format, opts) => {
                    const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
                    const eid = employeeId ?? "";
                    const targetLocationId = opts?.locationId ?? locationId;
                    let exportSize = staffTotalElements || 1000;
                    if (opts?.locationId) {
                        const probe: any = await getLabourStaffPerformanceApi({ locationId: targetLocationId, page: 1, size: 1, search: debouncedStaffSearch, sortBy: staffSortBy, sortAsc: staffSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) });
                        exportSize = Number(probe?.totalElements) || 1000;
                    }
                    const res: any = await getLabourStaffPerformanceApi({ locationId: targetLocationId, page: 1, size: exportSize, search: debouncedStaffSearch, sortBy: staffSortBy, sortAsc: staffSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) });
                    const list = mapStaffPerformance(Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []));
                    return {
                        headers: ["Employee Name", "Total Hours", "Break Hours", "Sales", "SPLH", "Tips", "Gratuity", "Void", "Void Amount"],
                        rows: [
                            ...list.map((e) => [e.empName, fmtHours(e.hours), fmtHours(e.breakHours), formatMoneyForExport(e.sales), e.splh == null ? "—" : formatMoneyForExport(e.splh), formatMoneyForExport(e.tips), formatMoneyForExport(e.gratuity), e.voidCount, formatMoneyForExport(e.voidAmount)]),
                            buildStaffPerformanceTotalRow(list, res?.summary),
                        ],
                        totalRow: true,
                    };
                }}
            />
            <WorkingHoursLog
                dateRangeLabel={exportDateRangeLabel}
                shifts={shifts}
                onSearch={(v: string) => setHoursSearch(v)}
                searchTerm={hoursSearch}
                loading={workingHoursLogLoading}
                serverPagination={{ totalElements: hoursTotalElements, page: hoursPage, pageSize: hoursPageSize, onPageChange: (p, s) => { setHoursPage(p); setHoursPageSize(s); } }}
                sortBy={hoursSortBy}
                sortDir={hoursSortDir}
                onSortChange={handleHoursSortChange}
                buildExport={async (_format, opts) => {
                    const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
                    const eid = employeeId ?? "";
                    const targetLocationId = opts?.locationId ?? locationId;
                    let exportSize = hoursTotalElements || 1000;
                    if (opts?.locationId) {
                        const probe: any = await getLabourWorkingHoursLogApi({ locationId: targetLocationId, page: 1, size: 1, search: debouncedHoursSearch, sortBy: hoursSortBy, sortAsc: hoursSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) });
                        exportSize = Number(probe?.totalElements) || 1000;
                    }
                    const res: any = await getLabourWorkingHoursLogApi({ locationId: targetLocationId, page: 1, size: exportSize, search: debouncedHoursSearch, sortBy: hoursSortBy, sortAsc: hoursSortDir === "asc", ...dateParams, ...(eid ? { employeeId: eid } : {}) });
                    const list = mapWorkingHoursLog(Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []));
                    return {
                        headers: ["Date", "Employee Name", "Clock-In", "Clock-Out", "Worked Hours", "Break Hours"],
                        rows: list.map((s) => [fmtShiftDate(s.date), s.empName, fmtClock(s.clockIn), s.clockOut ? fmtClock(s.clockOut) : "In Progress", fmtHours(s.hours), fmtHours(s.breakMinutes / 60)]),
                    };
                }}
            />
        </div>
    );

    return (
        <div style={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
            <TopBar variant="mobile" />
            {body}
            <BottomNav />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENTRY POINT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function LaborReport() {
    const isDesktop = useIsDesktop();
    const dispatch = useAppDispatch();
    const [period, setPeriod] = useSharedReportDateFilter("Today");
    const [employeeId, setEmployeeId] = useState<string | null>(null);
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? "", [auth, branch]);

    const labourEmployees = useAppSelector((s) => s.clickHouse.labourEmployees);
    const options = useMemo(
        () => labourEmployees.map((e) => ({ id: e.staffId, name: e.staffName })),
        [labourEmployees]
    );

    const lastEmployeesKey = useRef("");
    useEffect(() => {
        if (!locationId) return;
        const key = locationId;
        if (lastEmployeesKey.current === key) return;
        lastEmployeesKey.current = key;
        dispatch(fetchLabourEmployeesRequest({ locationId }));
    }, [locationId, dispatch]);

    const storeTz = useStoreTimeZone();
    const today = todayStr(storeTz);
    const yesterday = yesterdayStr(storeTz);
    const dateRange = useMemo(() => {
        return getStartEndFromFilter(period, today, yesterday, storeTz) ?? { startDate: today, endDate: today };
    }, [period, today, yesterday, storeTz]);

    useEffect(() => { track("maghil_reports_page_view"); }, []);

    const props = { period, setPeriod, employeeId, setEmployeeId, options, locationId, dateRange };
    return isDesktop ? <LaborDesktop {...props} /> : <LaborMobile {...props} />;
}
